import React, { useState, useCallback } from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import { useMembership, updateCustomerDetail } from '@techstyle/react-accounts';
import { useDomain, useSessionDetail } from '@techstyle/redux-core';

import { trackEmailOptInInteractions } from '../../actions/email';
import { EmailPreferences } from '../../constants/email';
import { parseEmailPreferencesPayload } from '../../utils/formatEmailPreferences';
import { useEmailPreferencesWithVars } from '../../utils/useEmailPreferencesWithVars';
import { useCountdownTimer } from '../CountdownTimerProvider/CountdownTimerProvider';
import { Component as EmailNotificationButton } from '../EmailNotificationButton';
import { Component as EmailNotificationModal } from '../EmailNotificationModal';

const emailOptInMarketingList = config.get('public.sailthru.emailOptIn');

function isEUDomain(tld) {
  switch (tld) {
    case '.co.uk':
    case '.de':
    case '.es':
    case '.eu':
    case '.fr':
      return true;
    default:
      return false;
  }
}

const EmailNotification = ({ onClickEmailBadge }) => {
  const dispatch = useDispatch();
  const emailPreferences = useEmailPreferencesWithVars();
  const membership = useMembership();
  const [isOpen, setOpen] = useState(false);
  const { tld } = useDomain();
  const { isCountdownActive } = useCountdownTimer();
  const handleShowModal = useCallback(() => setOpen(isOpen => !isOpen), []);
  const emailPreferenceValue = parseEmailPreferencesPayload(emailPreferences);
  const sessionDetail = useSessionDetail('email_opt_in_decline_offer');
  const isOfferDeclined =
    sessionDetail &&
    sessionDetail.networkStatus.isUpToDate &&
    sessionDetail.value;
  const isOptedIn =
    emailPreferences.lists &&
    emailPreferences.lists.includes(
      membership.isVip
        ? emailOptInMarketingList.vip[tld]
        : emailOptInMarketingList.lead[tld]
    );

  const shouldOptIn =
    emailPreferences.networkStatus.isUpToDate &&
    !isOptedIn &&
    emailPreferenceValue !== EmailPreferences.ALWAYS &&
    !isOfferDeclined;

  const shouldShowEmailNotification =
    (isOpen || shouldOptIn) && isEUDomain(tld) && !isCountdownActive;

  const handleClickEmailBadge = useCallback(() => {
    handleShowModal();
    dispatch(trackEmailOptInInteractions('Badge Click'));
    dispatch(updateCustomerDetail({ name: 'email_opt_in_badge', value: '' }));
    onClickEmailBadge && onClickEmailBadge();
  }, [dispatch, handleShowModal, onClickEmailBadge]);

  return shouldShowEmailNotification ? (
    <>
      <EmailNotificationButton onClick={handleClickEmailBadge} />
      <EmailNotificationModal
        isOpen={isOpen}
        onRequestClose={handleShowModal}
      />
    </>
  ) : null;
};

EmailNotification.propTypes = {
  onClickEmailBadge: PropTypes.func,
};

export default EmailNotification;
